/**
 * DEA controlled-substance schedule ("dea_schedule") labels.
 *
 * Raw values are "CI"/"CII"/"CIII"/"CIV"/"CV" — confirmed against FDA's own
 * NDC Product File Definitions ("This is the assigned DEA Schedule number
 * as reported by the labeler. Values are CI, CII, CIII, CIV, and CV.")
 * https://www.fda.gov/drugs/drug-approvals-and-databases/ndc-product-file-definitions
 *
 * Schedule meanings are the DEA Diversion Control Division's own wording.
 * https://www.deadiversion.usdoj.gov/schedules/schedules.html
 *
 * Mirrors the teCodeLabels.ts/applTypeLabels.ts pattern: one shared lookup
 * consumed by every surface that renders `dea_schedule` (2026-09-23
 * follow-up feedback — this field was previously shown as a bare, unexplained
 * code everywhere it appeared).
 */
export const DEA_SCHEDULES_URL =
  'https://www.deadiversion.usdoj.gov/schedules/schedules.html';

export interface DeaScheduleInfo {
  shortName: string;
  fullDescription: string;
}

export const DEA_SCHEDULE_LABELS: Record<string, DeaScheduleInfo> = {
  CI: {
    shortName: 'Schedule I',
    fullDescription:
      'Substances in this schedule have no currently accepted medical use in the United States, a lack of accepted safety for use under medical supervision, and a high potential for abuse.',
  },
  CII: {
    shortName: 'Schedule II',
    fullDescription:
      'Substances in this schedule have a high potential for abuse which may lead to severe psychological or physical dependence.',
  },
  CIII: {
    shortName: 'Schedule III',
    fullDescription:
      'Substances in this schedule have a potential for abuse less than substances in Schedules I or II and abuse may lead to moderate or low physical dependence or high psychological dependence.',
  },
  CIV: {
    shortName: 'Schedule IV',
    fullDescription:
      'Substances in this schedule have a low potential for abuse relative to substances in Schedule III.',
  },
  CV: {
    shortName: 'Schedule V',
    fullDescription:
      'Substances in this schedule have a low potential for abuse relative to substances listed in Schedule IV and consist primarily of preparations containing limited quantities of certain narcotics.',
  },
};

/** Resolves a raw dea_schedule code to its label info. Returns `undefined` for anything not CI-CV. */
export function getDeaScheduleInfo(code: string): DeaScheduleInfo | undefined {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return undefined;
  return DEA_SCHEDULE_LABELS[trimmed];
}

/** `"[code] - [Short name]"`, or the raw code if unrecognized. */
export function formatDeaSchedule(code: string | null): string {
  if (!code) return '—';
  const info = getDeaScheduleInfo(code);
  return info ? `${code} - ${info.shortName}` : code;
}
